import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { Button, Checkbox, Label, TextInput } from '@c2fo/liquidity';
import FilterDrawer from '../../FilterDrawer';
import DateRangePicker from '../shared/DateRangePicker';

export interface AdjustmentFilterOptions {
  originalDepositDate?: {
    from?: string | null;
    to?: string | null;
  };
  amount?: {
    from?: number | null;
    to?: number | null;
  };
  adjustmentType?: {
    matched?: boolean;
    unmatched?: boolean;
  };
}

const AdjustmentFilters = ({
  filterOpen,
  setFilterOpen,
  filters,
  handleFilters,
  currency,
}: {
  filterOpen: boolean;
  setFilterOpen: (open: boolean) => void;
  filters?: AdjustmentFilterOptions;
  handleFilters: (filters: AdjustmentFilterOptions) => void;
  currency: string;
}) => {
  const { t } = useTranslation();
  const { originalDepositDate, amount, adjustmentType } = filters ?? {};

  const [isFromDatePickerOpen, setIsFromDatePickerOpen] = useState(false);
  const [isToDatePickerOpen, setIsToDatePickerOpen] = useState(false);

  const [selectedDate, setSelectedDate] = useState<'allDates' | 'maturityDate'>(
    originalDepositDate ? 'maturityDate' : 'allDates'
  );

  // Local state for debounced input values for amount filters
  const [amountFromDebounce, setAmountFromDebounce] = useState<string>(amount?.from?.toString() || '');
  const [amountToDebounce, setAmountToDebounce] = useState<string>(amount?.to?.toString() || '');

  // If maturity date radio was selected but no From or To date selected, reset to All Dates radio on close
  // API calls it originalDepositDate but it is labeled maturity date in the UI
  useEffect(() => {
    if (!filterOpen && !originalDepositDate) {
      setSelectedDate('allDates');
    }
  }, [filterOpen, originalDepositDate]);

  useEffect(() => {
    // Sync local amount values when urlState filters change outside of filter drawer (e.g. Clear filters)
    if (!filterOpen) {
      setAmountFromDebounce(amount?.from?.toString() || '');
      setAmountToDebounce(amount?.to?.toString() || '');
    }
  }, [amount?.from, amount?.to, filterOpen]);

  useDebounce(
    () => {
      // Debounce call to update urlState when filter is open and user is typing
      // Checking for open filter prevents circular updates if user clicks Clear filters which updates local amount values from urlState
      if (filterOpen && amountFromDebounce !== '-') {
        handleFilters({ amount: { from: amountFromDebounce === '' ? null : Number(amountFromDebounce) } });
      }
    },
    500,
    [amountFromDebounce]
  );

  useDebounce(
    () => {
      if (filterOpen && amountToDebounce !== '-') {
        handleFilters({ amount: { to: amountToDebounce === '' ? null : Number(amountToDebounce) } });
      }
    },
    500,
    [amountToDebounce]
  );

  return (
    <FilterDrawer filterOpen={filterOpen} setFilterOpen={setFilterOpen}>
      <div className="space-y-4 p-4">
        {/* Date Filter */}
        <div className="w-full space-y-1">
          <Label>{t('core.dates')}</Label>
          <div className="rounded border border-stroke bg-canvas p-4">
            <ul className="space-y-2">
              {/* All Dates i.e. no date filter applied */}
              <li>
                <label htmlFor="allDates" className="flex cursor-pointer items-center gap-2">
                  <input
                    id="allDates"
                    className="h-5 w-5 accent-secondary-500 focus:ring-secondary-600"
                    checked={selectedDate === 'allDates'}
                    onChange={() => {
                      setSelectedDate('allDates');
                      handleFilters({
                        originalDepositDate: { from: null, to: null },
                      });
                    }}
                    type="radio"
                  />
                  {t('invoiceCentral.allDepositDates')}
                </label>
              </li>
              {/* Maturity Date */}
              <li>
                <label htmlFor="maturityDate" className="flex cursor-pointer items-center gap-2">
                  <input
                    id="maturityDate"
                    className="h-5 w-5 accent-secondary-500 focus:ring-secondary-600"
                    checked={selectedDate === 'maturityDate'}
                    onChange={() => {
                      setSelectedDate('maturityDate');
                    }}
                    type="radio"
                  />
                  {t('core.maturityDate')}
                </label>
                {/* API calls it originalDepositDate but it is labeled maturity date in the UI */}
                {selectedDate === 'maturityDate' && (
                  <DateRangePicker
                    to={originalDepositDate?.to ?? null}
                    from={originalDepositDate?.from ?? null}
                    onFromChange={(date) => handleFilters({ originalDepositDate: { from: date } })}
                    onToChange={(date) => handleFilters({ originalDepositDate: { to: date } })}
                    toOpen={isToDatePickerOpen}
                    fromOpen={isFromDatePickerOpen}
                    onToOpenChange={setIsToDatePickerOpen}
                    onFromOpenChange={setIsFromDatePickerOpen}
                    name="Maturity Date"
                  />
                )}
              </li>
            </ul>
          </div>
        </div>
        {/* Adjustment Amount Filter */}
        <div className="w-full space-y-1">
          <Label>{t(`core.adjustmentAmount`)}</Label>
          <div className="space-y-2 rounded border border-stroke bg-canvas p-4">
            <div>
              <Label htmlFor="from" className="capitalize">
                {t(`core.from`)}
              </Label>
              <div className="flex items-center gap-2">
                <TextInput
                  id="from"
                  name="from"
                  onChange={(e) => {
                    const num = Number(e.target.value);
                    if (e.target.value === '-' || !isNaN(num)) {
                      setAmountFromDebounce(e.target.value);
                    }
                  }}
                  size="sm"
                  type="text"
                  value={amountFromDebounce}
                />
                {currency}
              </div>
            </div>
            <div>
              <Label htmlFor="to" className="capitalize">
                {t(`core.to`)}
              </Label>
              <div className="flex items-center gap-2">
                <TextInput
                  id="to"
                  name="to"
                  onChange={(e) => {
                    const num = Number(e.target.value);
                    if (e.target.value === '-' || !isNaN(num)) {
                      setAmountToDebounce(e.target.value);
                    }
                  }}
                  size="sm"
                  type="text"
                  value={amountToDebounce}
                />
                {currency}
              </div>
            </div>
          </div>
        </div>
        {/* Adjustment Type Filter */}
        <div className="w-full space-y-1">
          <Label>{t(`core.adjustmentType`)}</Label>
          <div className="space-y-2 rounded border border-stroke bg-canvas p-4">
            <Checkbox
              checked={adjustmentType?.matched}
              color="secondary"
              id="matched"
              label={t('taker.invoices.adjustment.statusMatched')}
              onChange={(e) => handleFilters({ adjustmentType: { matched: e.target.checked } })}
              value="matched"
            />
            <Checkbox
              checked={adjustmentType?.unmatched}
              color="secondary"
              id="unmatched"
              label={t('taker.invoices.adjustment.statusUnmatched')}
              onChange={(e) => handleFilters({ adjustmentType: { unmatched: e.target.checked } })}
              value="unmatched"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end px-4 pb-4">
        <Button onClick={() => setFilterOpen(false)} variant="secondary">
          {t('core.close')}
        </Button>
      </div>
    </FilterDrawer>
  );
};

export default AdjustmentFilters;
